import { request } from '@umijs/max';

export type AdminRole = 'super_admin' | 'admin';

export type NoraAdmin = {
  id: number;
  username: string;
  email?: string;
  realName?: string;
  real_name?: string;
  role: AdminRole;
  permissions?: string[];
  menuPermissions?: string[];
};

export type NoraLoginResult = {
  token: string;
  admin: NoraAdmin;
};

export type NoraResponse<T = unknown> = {
  success: boolean;
  message?: string;
  data: T;
};

const TOKEN_KEY = 'admin_token';
const ADMIN_KEY = 'admin_info';

export function getToken() {
  return localStorage.getItem(TOKEN_KEY);
}

export function setAuth(token: string, admin: NoraAdmin) {
  localStorage.setItem(TOKEN_KEY, token);
  localStorage.setItem(ADMIN_KEY, JSON.stringify(admin));
  document.cookie = `${TOKEN_KEY}=${token}; path=/; max-age=${7 * 24 * 60 * 60}`;
}

export function clearAuth() {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(ADMIN_KEY);
  document.cookie = `${TOKEN_KEY}=; path=/; max-age=0`;
}

export function getStoredAdmin(): NoraAdmin | undefined {
  const raw = localStorage.getItem(ADMIN_KEY);
  if (!raw) return undefined;

  try {
    return JSON.parse(raw) as NoraAdmin;
  } catch {
    clearAuth();
    return undefined;
  }
}

export function isTokenAlive(token?: string | null) {
  if (!token) return false;

  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return Date.now() < Number(payload.exp) * 1000;
  } catch {
    return false;
  }
}

export async function login(username: string, password: string) {
  const res = await request<NoraResponse<NoraLoginResult>>(
    '/api/admin/auth/login',
    {
      method: 'POST',
      data: { username, password },
      skipErrorHandler: true,
    },
  );

  if (res.success && res.data?.token) {
    setAuth(res.data.token, res.data.admin);
  }

  return res;
}

export async function logout() {
  try {
    await request<NoraResponse>('/api/admin/auth/logout', {
      method: 'POST',
      skipErrorHandler: true,
    });
  } finally {
    clearAuth();
  }
}

export async function currentAdmin() {
  const res = await request<NoraResponse<{ admin: NoraAdmin }>>(
    '/api/admin/auth/me',
    {
      method: 'GET',
      skipErrorHandler: true,
    },
  );
  if (res.success && res.data?.admin) {
    localStorage.setItem(ADMIN_KEY, JSON.stringify(res.data.admin));
    return res.data.admin;
  }
  return undefined;
}

export async function changePassword(oldPassword: string, newPassword: string) {
  return request<NoraResponse>('/api/admin/auth/change-password', {
    method: 'POST',
    data: { oldPassword, newPassword },
    skipErrorHandler: true,
  });
}
